import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Plus, Search, AlertTriangle, ArrowDown, ArrowUp, Settings, Trash2, Pencil, BoxesIcon, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIAS_DEFAULT = ['General', 'Aceites', 'Filtros', 'Frenos', 'Suspension', 'Electrico', 'Quimicos', 'Limpieza', 'Herramientas', 'Otro'];
const UNIDADES = ['pieza', 'litro', 'galon', 'kilo', 'metro', 'rollo', 'caja', 'paquete'];

export default function Inventario() {
  const [productos, setProductos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [filtroCat, setFiltroCat] = useState('');
  const [filtroStock, setFiltroStock] = useState(false);
  const [dialog, setDialog] = useState(null); // 'nuevo' | 'editar' | 'movimiento'
  const [productoActual, setProductoActual] = useState(null);
  const [form, setForm] = useState({});
  const [movForm, setMovForm] = useState({ producto_id: '', tipo: 'entrada', cantidad: '', nota: '' });
  const [guardando, setGuardando] = useState(false);
  const [movimientos, setMovimientos] = useState([]);
  const [verMovimientos, setVerMovimientos] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const params = {};
      if (buscar) params.buscar = buscar;
      if (filtroCat && filtroCat !== 'todas') params.categoria = filtroCat;
      if (filtroStock) params.stock_bajo = true;
      const [pRes, rRes, cRes] = await Promise.all([
        api.get('/inventario/productos', { params }),
        api.get('/inventario/resumen'),
        api.get('/inventario/categorias'),
      ]);
      setProductos(pRes.data);
      setResumen(rRes.data);
      setCategorias(cRes.data);
    } catch { toast.error('Error al cargar inventario'); }
    finally { setCargando(false); }
  }, [buscar, filtroCat, filtroStock]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setForm({ nombre: '', sku: '', categoria: 'General', descripcion: '', unidad: 'pieza',
      precio_compra: '', precio_venta: '', stock: '', stock_minimo: '5', proveedor: '' });
    setDialog('nuevo');
  };

  const abrirEditar = (p) => {
    setProductoActual(p);
    setForm({ ...p });
    setDialog('editar');
  };

  const abrirMovimiento = (p) => {
    setProductoActual(p);
    setMovForm({ producto_id: p.id, tipo: 'entrada', cantidad: '', nota: '' });
    setDialog('movimiento');
  };

  const abrirHistorial = async (p) => {
    setProductoActual(p);
    try {
      const res = await api.get('/inventario/movimientos', { params: { producto_id: p.id, limit: 30 } });
      setMovimientos(res.data);
      setVerMovimientos(p.id);
    } catch { toast.error('Error al cargar historial'); }
  };

  const guardar = async () => {
    if (!form.nombre?.trim()) { toast.error('El nombre es obligatorio'); return; }
    setGuardando(true);
    try {
      if (dialog === 'nuevo') {
        await api.post('/inventario/productos', form);
        toast.success('Producto creado');
      } else {
        await api.put(`/inventario/productos/${productoActual.id}`, form);
        toast.success('Producto actualizado');
      }
      setDialog(null);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!confirm('Eliminar este producto?')) return;
    try { await api.delete(`/inventario/productos/${id}`); toast.success('Eliminado'); cargar(); }
    catch { toast.error('Error al eliminar'); }
  };

  const registrarMovimiento = async () => {
    if (!movForm.cantidad || parseInt(movForm.cantidad) <= 0) {
      toast.error('La cantidad debe ser mayor a 0'); return;
    }
    setGuardando(true);
    try {
      await api.post('/inventario/movimientos', movForm);
      toast.success(`${movForm.tipo === 'entrada' ? 'Entrada' : movForm.tipo === 'salida' ? 'Salida' : 'Ajuste'} registrada`);
      setDialog(null);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setGuardando(false); }
  };

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-64 bg-slate-800" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inventario-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Package className="w-8 h-8 text-amber-400" /> Inventario
        </h1>
        <Button onClick={abrirNuevo} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nuevo-producto-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
        </Button>
      </div>

      {/* Summary cards */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-cyan-400">{resumen.total_items}</p>
            <p className="text-[10px] text-slate-500 uppercase">Productos</p>
          </CardContent></Card>
          <Card className={`border-slate-800 ${resumen.stock_bajo > 0 ? 'bg-amber-500/5 border-amber-800/50' : 'bg-slate-900/40'}`}><CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${resumen.stock_bajo > 0 ? 'text-amber-400' : 'text-slate-400'}`}>{resumen.stock_bajo}</p>
            <p className="text-[10px] text-slate-500 uppercase">Stock Bajo</p>
          </CardContent></Card>
          <Card className={`border-slate-800 ${resumen.sin_stock > 0 ? 'bg-red-500/5 border-red-800/50' : 'bg-slate-900/40'}`}><CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${resumen.sin_stock > 0 ? 'text-red-400' : 'text-slate-400'}`}>{resumen.sin_stock}</p>
            <p className="text-[10px] text-slate-500 uppercase">Sin Stock</p>
          </CardContent></Card>
          <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-400">{formatoMXN(resumen.valor_total)}</p>
            <p className="text-[10px] text-slate-500 uppercase">Valor Compra</p>
          </CardContent></Card>
          <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-400">{formatoMXN(resumen.valor_venta)}</p>
            <p className="text-[10px] text-slate-500 uppercase">Valor Venta</p>
          </CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={buscar} onChange={e => setBuscar(e.target.value)}
            placeholder="Buscar por nombre o SKU..."
            className="pl-10 bg-slate-900 border-slate-700 text-slate-200"
            data-testid="inventario-buscar" />
        </div>
        <Select value={filtroCat} onValueChange={setFiltroCat}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="filtro-categoria">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="todas" className="text-slate-200">Todas</SelectItem>
            {[...new Set([...CATEGORIAS_DEFAULT, ...categorias])].sort().map(c => (
              <SelectItem key={c} value={c} className="text-slate-200">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant={filtroStock ? 'default' : 'outline'} size="sm"
          onClick={() => setFiltroStock(!filtroStock)}
          className={filtroStock ? 'bg-amber-500 text-black' : 'border-slate-700 text-slate-400'}
          data-testid="filtro-stock-bajo">
          <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Stock Bajo
        </Button>
      </div>

      {/* Products list */}
      {productos.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="py-16 text-center">
            <BoxesIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">{buscar || filtroCat ? 'Sin resultados' : 'Agrega tu primer producto'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {productos.map(p => {
            const bajStock = p.stock <= p.stock_minimo;
            const sinStock = p.stock <= 0;
            return (
              <Card key={p.id} className={`border-slate-800 hover:border-slate-700 transition-colors ${sinStock ? 'bg-red-500/5 border-red-900/30' : bajStock ? 'bg-amber-500/5 border-amber-900/30' : 'bg-slate-900/40'}`}
                data-testid={`producto-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-slate-100">{p.nombre}</span>
                        {p.sku && <Badge variant="outline" className="text-slate-500 border-slate-700 text-[10px] font-mono">{p.sku}</Badge>}
                        <Badge className="text-[10px] bg-slate-800 text-slate-400 border-slate-700">{p.categoria}</Badge>
                        {sinStock && <Badge className="bg-red-500/20 text-red-400 text-[10px]">SIN STOCK</Badge>}
                        {!sinStock && bajStock && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">STOCK BAJO</Badge>}
                        {p.visible_mostrador && <Badge className="bg-cyan-500/20 text-cyan-400 text-[10px]" data-testid={`badge-mostrador-${p.id}`}>EN MOSTRADOR</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>Stock: <span className={`font-bold ${sinStock ? 'text-red-400' : bajStock ? 'text-amber-400' : 'text-cyan-400'}`}>{p.stock}</span> / min: {p.stock_minimo}</span>
                        <span>Compra: {formatoMXN(p.precio_compra)}</span>
                        <span>Venta: <span className="text-amber-400">{formatoMXN(p.precio_venta)}</span></span>
                        {p.proveedor && <span>Prov: {p.proveedor}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => abrirMovimiento(p)}
                        className="text-emerald-400 hover:text-emerald-300 text-xs" data-testid={`mov-entrada-${p.id}`}>
                        <ArrowDown className="w-3.5 h-3.5 mr-1" /> Entrada
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { abrirMovimiento(p); setMovForm(f => ({...f, tipo: 'salida'})); }}
                        className="text-orange-400 hover:text-orange-300 text-xs" data-testid={`mov-salida-${p.id}`}>
                        <ArrowUp className="w-3.5 h-3.5 mr-1" /> Salida
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => abrirHistorial(p)}
                        className="text-slate-400 hover:text-slate-300 text-xs" data-testid={`historial-${p.id}`}>
                        <TrendingDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => abrirEditar(p)}
                        className="text-cyan-400 hover:text-cyan-300 text-xs" data-testid={`editar-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => eliminar(p.id)}
                        className="text-red-400 hover:text-red-300 text-xs" data-testid={`eliminar-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  {/* Inline history */}
                  {verMovimientos === p.id && movimientos.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-800">
                      <p className="text-xs text-slate-500 mb-2 uppercase">Ultimos movimientos</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {movimientos.map(m => (
                          <div key={m.id} className="flex items-center justify-between text-xs bg-slate-800/30 px-2 py-1 rounded-sm">
                            <div className="flex items-center gap-2">
                              {m.tipo === 'entrada' ? <ArrowDown className="w-3 h-3 text-emerald-400" /> :
                               m.tipo === 'salida' ? <ArrowUp className="w-3 h-3 text-orange-400" /> :
                               <Settings className="w-3 h-3 text-cyan-400" />}
                              <span className="text-slate-400 capitalize">{m.tipo}</span>
                              <span className="font-bold text-slate-200">x{m.cantidad}</span>
                              {m.nota && <span className="text-slate-600">- {m.nota}</span>}
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <span>{m.stock_anterior} &rarr; {m.stock_nuevo}</span>
                              <span>{new Date(m.created_at).toLocaleDateString('es-MX')}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setVerMovimientos(null)}
                        className="text-slate-500 text-xs mt-1">Cerrar historial</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New/Edit Product Dialog */}
      <Dialog open={dialog === 'nuevo' || dialog === 'editar'} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="producto-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100">
              {dialog === 'nuevo' ? 'Nuevo Producto' : 'Editar Producto'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
                <Input value={form.nombre || ''} onChange={e => setForm({...form, nombre: e.target.value})}
                  placeholder="Ej: Aceite 5W-30" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-nombre" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">SKU</Label>
                <Input value={form.sku || ''} onChange={e => setForm({...form, sku: e.target.value.toUpperCase()})}
                  placeholder="Ej: ACE-001" className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="producto-sku" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Categoria</Label>
                <Select value={form.categoria || 'General'} onValueChange={v => setForm({...form, categoria: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {CATEGORIAS_DEFAULT.map(c => <SelectItem key={c} value={c} className="text-slate-200">{c}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Unidad</Label>
                <Select value={form.unidad || 'pieza'} onValueChange={v => setForm({...form, unidad: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-unidad">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {UNIDADES.map(u => <SelectItem key={u} value={u} className="text-slate-200 capitalize">{u}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Precio Compra</Label>
                <Input type="number" value={form.precio_compra || ''} onChange={e => setForm({...form, precio_compra: e.target.value})}
                  placeholder="0" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-precio-compra" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Precio Venta</Label>
                <Input type="number" value={form.precio_venta || ''} onChange={e => setForm({...form, precio_venta: e.target.value})}
                  placeholder="0" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-precio-venta" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Stock Minimo</Label>
                <Input type="number" value={form.stock_minimo || ''} onChange={e => setForm({...form, stock_minimo: e.target.value})}
                  placeholder="5" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-stock-min" /></div>
            </div>
            {dialog === 'nuevo' && (
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Stock Inicial</Label>
                <Input type="number" value={form.stock || ''} onChange={e => setForm({...form, stock: e.target.value})}
                  placeholder="0" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-stock-inicial" /></div>
            )}
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Proveedor</Label>
              <Input value={form.proveedor || ''} onChange={e => setForm({...form, proveedor: e.target.value})}
                placeholder="Ej: Distribuidor X" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="producto-proveedor" /></div>

            {/* Toggle visible en mostrador (POS WashPro) */}
            <div className="pt-2 border-t border-slate-800">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="producto-visible-mostrador-toggle">
                <input type="checkbox"
                  checked={!!form.visible_mostrador}
                  onChange={e => setForm({...form, visible_mostrador: e.target.checked})}
                  className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-900 text-cyan-500 focus:ring-cyan-500" />
                <div className="flex-1">
                  <div className="text-sm text-slate-200 font-medium">Mostrar en Mostrador (POS Carwash)</div>
                  <div className="text-[11px] text-slate-500 leading-relaxed">
                    {form.visible_mostrador
                      ? 'Se agregará al cobrar un turno de carwash. El stock se descontará automáticamente.'
                      : 'Solo aparece en el inventario. No se puede vender desde el mostrador.'}
                  </div>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardar} disabled={guardando} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-producto-btn">
              {guardando ? 'Guardando...' : dialog === 'nuevo' ? 'Crear Producto' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={dialog === 'movimiento'} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" data-testid="movimiento-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100">
              Movimiento de Stock
            </DialogTitle>
          </DialogHeader>
          {productoActual && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-800/50 rounded-sm p-3">
                <p className="text-sm text-slate-200 font-medium">{productoActual.nombre}</p>
                <p className="text-xs text-slate-500">Stock actual: <span className="text-cyan-400 font-bold">{productoActual.stock}</span></p>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Tipo</Label>
                <Select value={movForm.tipo} onValueChange={v => setMovForm({...movForm, tipo: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="mov-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="entrada" className="text-emerald-400">Entrada (compra)</SelectItem>
                    <SelectItem value="salida" className="text-orange-400">Salida (uso/venta)</SelectItem>
                    <SelectItem value="ajuste" className="text-cyan-400">Ajuste (correccion)</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Cantidad</Label>
                <Input type="number" min="1" value={movForm.cantidad} onChange={e => setMovForm({...movForm, cantidad: e.target.value})}
                  placeholder="0" className="bg-slate-950 border-slate-700 text-slate-200 text-lg font-bold text-center" data-testid="mov-cantidad" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nota</Label>
                <Input value={movForm.nota} onChange={e => setMovForm({...movForm, nota: e.target.value})}
                  placeholder="Ej: Compra a proveedor" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="mov-nota" /></div>
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-slate-400">Cancelar</Button>
            <Button onClick={registrarMovimiento} disabled={guardando}
              className={`font-bold ${movForm.tipo === 'entrada' ? 'bg-emerald-500 hover:bg-emerald-600 text-black' : movForm.tipo === 'salida' ? 'bg-orange-500 hover:bg-orange-600 text-black' : 'bg-cyan-500 hover:bg-cyan-600 text-black'}`}
              data-testid="registrar-mov-btn">
              {guardando ? 'Registrando...' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
